/** Wizard step 3 — onboarding profile.
 *
 * Collects the branching questions that drive `config.onboarding` on the
 * backend, plus the enabled languages. Reviews come first (D39: asked early —
 * the answer selects the reviews pack, REVIEW_CONFIG and the survey cron gate). The tag-category-set default is derived
 * from the chosen restech (the set whose `restech` matches, else the `standard`
 * set); the ag-template-group options come from `/agroup_template_groups/`.
 *
 * "Has a mobile app" is a checkbox; on the setup page of a licensee or a sandbox
 * (`appChoice`) it is a choice of three: inherit (the parent's / the live
 * operator's answer), yes, no.
 */
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import type { HasAppUpstream } from '@/api/preflight';
import { rbacApi } from '@/api/rbac';
import * as tagCategorySetsApi from '@/api/tagCategorySets';

import { Step7I18n } from './Step7I18n';
import type { Channel, Restech, WizardData } from './types';

interface Props {
  data: WizardData;
  onChange: (partial: Partial<WizardData>) => void;
  /** Setup-page reuse: languages live in config.i18n (the I18N_CONFIG item). */
  hideLanguages?: boolean;
  /** False when editing an EXISTING profile — a stored tag-set choice must not
   *  be clobbered by the restech-derived default on mount. */
  deriveTagSetOnMount?: boolean;
  /** Setup page of a tenant that can inherit has_app (a licensee, a sandbox of a live operator):
   *  the answer becomes inherit / yes / no. `data.has_app` stays the effective answer. */
  appChoice?: AppChoice;
}

export interface AppChoice {
  /** Whom a null answer inherits from, with that operator's answer. */
  from: HasAppUpstream;
  /** The tenant's own answer; null = inherited. */
  own: boolean | null;
  /** A sandbox: inheriting is presentation only (its guests see live's app); "yes" is its OWN app — its
   *  app setup items (TestFlight / internal testing builds). */
  sandbox: boolean;
  onChange: (own: boolean | null) => void;
}

const yesNo = (v: boolean) => (v ? 'yes' : 'no');

function AppChoiceField({ choice }: { choice: AppChoice }) {
  const { from, own, sandbox } = choice;
  const value = own === null ? 'inherit' : own ? 'yes' : 'no';
  return (
    <FormControl size="small" sx={{ maxWidth: 420, mb: 1 }}>
      <InputLabel id="has-app-label">Mobile app</InputLabel>
      <Select
        labelId="has-app-label"
        label="Mobile app"
        value={value}
        onChange={(e) => choice.onChange(e.target.value === 'inherit' ? null : e.target.value === 'yes')}
      >
        <MenuItem value="inherit">
          Same as {from.code} ({yesNo(from.has_app)})
        </MenuItem>
        <MenuItem value="yes">{sandbox ? 'Yes — its own app (test builds)' : 'Yes'}</MenuItem>
        <MenuItem value="no">No</MenuItem>
      </Select>
      <FormHelperText>
        {sandbox
          ? `Its guests see ${from.name}’s app unless this says otherwise; its own app setup items appear only with “Yes”.`
          : `A licensee uses its franchise parent’s (${from.name}) app unless this says otherwise.`}
      </FormHelperText>
    </FormControl>
  );
}

const RESTECH_OPTIONS: { value: Restech; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'fareharbor', label: 'FareHarbor' },
  { value: 'ventrata', label: 'Ventrata' },
  { value: 'bokun', label: 'Bókun' },
  { value: 'rezdy', label: 'Rezdy' },
  { value: 'trekksoft', label: 'TrekkSoft' },
  { value: 'xml', label: 'XML' },
  { value: 'other', label: 'Other' },
];

const CHANNEL_OPTIONS: { value: Channel; label: string }[] = [
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'push', label: 'Push notifications' },
];

const STANDARD_SET_CODE = 'standard';

export function StepProfile({
  data,
  onChange,
  hideLanguages = false,
  deriveTagSetOnMount = true,
  appChoice,
}: Props) {
  const tagSetsQuery = useQuery({
    queryKey: ['tagCategorySets', 'all'],
    queryFn: () => tagCategorySetsApi.list({ pageSize: 200 }),
  });
  const tagSets = useMemo(() => tagSetsQuery.data?.results ?? [], [tagSetsQuery.data]);

  const templateGroupsQuery = useQuery({
    queryKey: ['agroupTemplateGroups', 'all'],
    queryFn: () => rbacApi.listGroups({ pageSize: 200 }),
  });
  const templateGroups = templateGroupsQuery.data?.results ?? [];

  // Derive the tag-category-set default from restech: prefer the set whose
  // `restech` matches, else the `standard` set. Recomputed whenever restech
  // changes (tracked by ref so a manual override isn't clobbered on re-render).
  const lastRestech = useRef<Restech | null>(deriveTagSetOnMount ? null : data.restech);
  useEffect(() => {
    if (tagSets.length === 0) return;
    if (lastRestech.current === data.restech) return;
    lastRestech.current = data.restech;
    const match =
      tagSets.find((s) => s.restech === data.restech) ??
      tagSets.find((s) => s.code === STANDARD_SET_CODE);
    if (match && match.id !== data.tag_category_set) {
      onChange({ tag_category_set: match.id });
    }
  }, [data.restech, data.tag_category_set, tagSets, onChange]);

  function toggleChannel(channel: Channel, checked: boolean) {
    const next = checked
      ? [...data.channels, channel]
      : data.channels.filter((c) => c !== channel);
    onChange({ channels: next });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Reviews
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.reviews_enabled}
                onChange={(e) => onChange({ reviews_enabled: e.target.checked })}
              />
            }
            label="Send post-trip review requests"
          />
          <Collapse in={data.reviews_enabled} unmountOnExit>
            <Box sx={{ pl: 4, py: 1 }}>
              <TextField
                label="Negative-review alert inbox"
                type="email"
                size="small"
                fullWidth
                sx={{ maxWidth: 420 }}
                value={data.reviews_alert_email}
                onChange={(e) => onChange({ reviews_alert_email: e.target.value })}
                helperText="Operator e-mail that receives the negative-review alert. Leave blank for none."
              />
            </Box>
          </Collapse>
        </FormGroup>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Booking system
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="restech-label">Reservation system</InputLabel>
              <Select
                labelId="restech-label"
                label="Reservation system"
                value={data.restech}
                onChange={(e) => onChange({ restech: e.target.value as Restech })}
              >
                {RESTECH_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="cadence-label">Trip cadence</InputLabel>
              <Select
                labelId="cadence-label"
                label="Trip cadence"
                value={data.cadence}
                onChange={(e) => onChange({ cadence: e.target.value as WizardData['cadence'] })}
              >
                <MenuItem value="day">Single-day trips</MenuItem>
                <MenuItem value="multi_day">Multi-day trips</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="tag-set-label">Tag category set</InputLabel>
              <Select
                labelId="tag-set-label"
                label="Tag category set"
                value={data.tag_category_set ?? ''}
                onChange={(e) =>
                  onChange({ tag_category_set: e.target.value === '' ? null : Number(e.target.value) })
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {tagSets.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label || s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="ag-group-label">RBAC template group</InputLabel>
              <Select
                labelId="ag-group-label"
                label="RBAC template group"
                value={data.ag_template_group ?? ''}
                onChange={(e) =>
                  onChange({ ag_template_group: e.target.value === '' ? null : Number(e.target.value) })
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {templateGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Product &amp; operations
        </Typography>
        <FormGroup>
          {appChoice ? (
            <AppChoiceField choice={appChoice} />
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.has_app}
                  onChange={(e) => onChange({ has_app: e.target.checked })}
                />
              }
              label="Tour operator has a mobile app"
            />
          )}
          <Collapse in={data.has_app} unmountOnExit>
            <Box sx={{ pl: 4, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl size="small" sx={{ maxWidth: 320 }}>
                <InputLabel id="app-store-label">App store account</InputLabel>
                <Select
                  labelId="app-store-label"
                  label="App store account"
                  value={data.app_store_account}
                  onChange={(e) =>
                    onChange({ app_store_account: e.target.value as WizardData['app_store_account'] })
                  }
                >
                  <MenuItem value="curated_planet">Curated Planet</MenuItem>
                  <MenuItem value="operator">Operator&apos;s own</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={data.has_existing_apps}
                    onChange={(e) => onChange({ has_existing_apps: e.target.checked })}
                  />
                }
                label="Operator already has published apps"
              />
            </Box>
          </Collapse>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.guides_split_chat}
                onChange={(e) => onChange({ guides_split_chat: e.target.checked })}
              />
            }
            label="Guides use a separate chat from guests"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data.vehicle_tracking}
                onChange={(e) => onChange({ vehicle_tracking: e.target.checked })}
              />
            }
            label="Vehicle tracking enabled"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={data.own_corporate_email}
                onChange={(e) => onChange({ own_corporate_email: e.target.checked })}
              />
            }
            label="Operator has its own corporate email domain"
          />
        </FormGroup>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Guest communication channels
        </Typography>
        <FormGroup row>
          {CHANNEL_OPTIONS.map((c) => (
            <FormControlLabel
              key={c.value}
              control={
                <Checkbox
                  checked={data.channels.includes(c.value)}
                  onChange={(e) => toggleChannel(c.value, e.target.checked)}
                />
              }
              label={c.label}
            />
          ))}
        </FormGroup>
      </Box>

      {!hideLanguages && (
        <>
          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Languages
            </Typography>
            <Step7I18n data={data} onChange={onChange} />
          </Box>
        </>
      )}
    </Box>
  );
}
